import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';

interface StableInputProps {
  value: string | number;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onMouseUp?: () => void;
  onKeyUp?: () => void;
  placeholder?: string;
  type?: 'text' | 'number' | 'email' | 'url' | 'password';
  className?: string;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  style?: React.CSSProperties;
}

/**
 * A stable input component that prevents cursor flickering and focus issues
 * by managing local state and debouncing updates
 */
export const StableInput = React.forwardRef<HTMLInputElement, StableInputProps>(({
  value,
  onChange,
  onBlur,
  onMouseUp,
  onKeyUp,
  placeholder,
  type = 'text',
  className = '',
  min,
  max,
  step,
  disabled = false,
  autoFocus = false,
  id,
  style,
}, ref) => {
  const [localValue, setLocalValue] = useState(String(value || ''));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const lastExternalValueRef = useRef(String(value || ''));

  // Sync localValue with external value changes (like variable insertion)
  // Only sync when the external value actually changed (not from our own onChange)
  useEffect(() => {
    const newValue = String(value || '');
    if (newValue !== lastExternalValueRef.current) {
      lastExternalValueRef.current = newValue;
      // CRITICAL FIX: Always sync external changes, even when focused
      // This allows variable insertion to work immediately
      setLocalValue(newValue);
    }
  }, [value]);

  // Debounced onChange to prevent excessive updates
  const debouncedOnChange = useCallback((newValue: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      lastExternalValueRef.current = newValue;
      onChange(newValue);
    }, 100);
  }, [onChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  }, [debouncedOnChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    
    // Clear any pending debounced updates and apply immediately
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Ensure the final value is applied
    const finalValue = localValue;
    lastExternalValueRef.current = finalValue;
    onChange(finalValue);
    
    onBlur?.();
  }, [localValue, onChange, onBlur]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <input
      ref={ref || inputRef}
      type={type}
      value={localValue}
      onChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseUp={onMouseUp}
      onKeyUp={onKeyUp}
      placeholder={placeholder}
      className={className}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      autoFocus={autoFocus}
      id={id}
      style={style}
    />
  );
});

StableInput.displayName = 'StableInput';

interface StableTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onMouseUp?: () => void;
  onKeyUp?: () => void;
  placeholder?: string;
  className?: string;
  rows?: number;
  cols?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  maxLength?: number;
  wrap?: 'hard' | 'soft' | 'off';
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  style?: React.CSSProperties;
}

/**
 * A stable textarea component that prevents cursor flickering and focus issues
 * by managing local state and debouncing updates
 */
export const StableTextarea = React.forwardRef<HTMLTextAreaElement, StableTextareaProps>(({
  value,
  onChange,
  onBlur,
  onMouseUp,
  onKeyUp,
  placeholder,
  className = '',
  rows = 3,
  cols,
  disabled = false,
  autoFocus = false,
  id,
  maxLength,
  wrap = 'soft',
  resize = 'none',
  style,
}, ref) => {
  const [localValue, setLocalValue] = useState(String(value || ''));
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const lastExternalValueRef = useRef(String(value || ''));

  // Sync localValue with external value changes (like variable insertion)
  // Only sync when the external value actually changed (not from our own onChange)
  useEffect(() => {
    const newValue = String(value || '');
    if (newValue !== lastExternalValueRef.current) {
      lastExternalValueRef.current = newValue;
      // CRITICAL FIX: Always sync external changes, even when focused
      // This allows variable insertion to work immediately
      setLocalValue(newValue);
    }
  }, [value]);

  // Debounced onChange to prevent excessive updates
  const debouncedOnChange = useCallback((newValue: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      lastExternalValueRef.current = newValue;
      onChange(newValue);
    }, 100);
  }, [onChange]);

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  }, [debouncedOnChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    
    // Clear any pending debounced updates and apply immediately
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Ensure the final value is applied
    const finalValue = localValue;
    lastExternalValueRef.current = finalValue;
    onChange(finalValue);
    
    onBlur?.();
  }, [localValue, onChange, onBlur]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Apply resize style and merge with passed style
  const textareaStyle = useMemo((): React.CSSProperties => ({
    resize: resize,
    overflow: 'visible',
    minHeight: '60px',
    width: '100%',
    ...style,
  }), [resize, style]);

  return (
    <textarea
      ref={ref || textareaRef}
      value={localValue}
      onChange={handleTextareaChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseUp={onMouseUp}
      onKeyUp={onKeyUp}
      placeholder={placeholder}
      className={className}
      rows={rows}
      cols={cols}
      disabled={disabled}
      autoFocus={autoFocus}
      id={id}
      maxLength={maxLength}
      wrap={wrap}
      style={textareaStyle}
    />
  );
});

StableTextarea.displayName = 'StableTextarea';

// Export both components
export default StableInput;
